/**
 * AP-29-H — Plätze verteilen.
 *
 * Die Arbeitsteilung aus AP-29: Der **Betreiber** verkauft die Platzzahl, der
 * **Mandant** verteilt sie. Diese Seite ist die zweite Hälfte — eine
 * Häkchenliste mit laufendem Zähler, mit der ein Betrieb dreißig Mitarbeiter in
 * einem Durchgang freischaltet.
 *
 * Bewusst **keine** Listen-Standard-Seite: Das ist keine Auswertung, die man
 * filtert, sortiert und exportiert, sondern eine Auswahl mit einem Limit. Die
 * Mitarbeiterliste eines Betriebs ist außerdem kurz genug, um sie ganz zu zeigen.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Users, Check } from 'lucide-react'
import { formCard, pageTitle, pageWide } from '@/lib/form-styles'
import { moduleSeatsApi, type SeatOverview } from '../api/module-seats.api'
import { useWarnungBeimVerlassen } from '@/lib/speichern'

export default function ModuleSeatsPage() {
  const { moduleKey = '' } = useParams<{ moduleKey: string }>()
  const { t } = useTranslation()

  const [stand, setStand] = useState<SeatOverview | null>(null)
  const [auswahl, setAuswahl] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(true)
  const [speichert, setSpeichert] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  const [gespeichert, setGespeichert] = useState(false)

  const laden = useCallback(async () => {
    setLoading(true)
    setFehler(null)
    try {
      const daten = await moduleSeatsApi.getOverview(moduleKey)
      setStand(daten)
      setAuswahl(new Set(daten.users.filter((u) => u.assigned).map((u) => u.id)))
    } catch (e: unknown) {
      const antwort = (e as { response?: { data?: { message?: string } } })?.response?.data?.message
      setFehler(antwort ?? 'Der Platzstand konnte nicht geladen werden.')
    } finally {
      setLoading(false)
    }
  }, [moduleKey])

  useEffect(() => { void laden() }, [laden])

  const umschalten = (id: string) => {
    setGespeichert(false)
    setAuswahl((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const speichern = async () => {
    setSpeichert(true)
    setFehler(null)
    try {
      await moduleSeatsApi.setSeats(moduleKey, [...auswahl])
      setGespeichert(true)
      await laden()
    } catch (e: unknown) {
      // Die Meldung des Servers ist konkreter als jede, die hier stehen könnte
      // („4 Zuteilungen passen nicht in 3 Plätze").
      const antwort = (e as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message
      setFehler(Array.isArray(antwort) ? antwort.join(' · ') : (antwort ?? 'Speichern fehlgeschlagen.'))
    } finally {
      setSpeichert(false)
    }
  }

  const frei = useMemo(() => {
    if (!stand?.module.seats) return null
    return stand.module.seats - auswahl.size
  }, [stand, auswahl])

  const geaendert = useMemo(() => {
    if (!stand) return false
    const vorher = new Set(stand.users.filter((u) => u.assigned).map((u) => u.id))
    if (vorher.size !== auswahl.size) return true
    for (const id of auswahl) if (!vorher.has(id)) return true
    return false
  }, [stand, auswahl])
  // AP-36 5.2: Ausnahme mit Knopf — ungespeichert warnt der Browser beim Schließen.
  useWarnungBeimVerlassen(geaendert)

  if (loading && !stand) {
    return <div className={pageWide}><p className="text-muted-foreground">Laden…</p></div>
  }

  const name = stand ? t(`modules.names.${stand.module.key}`) : moduleKey

  return (
    <div className={pageWide}>
      <div>
        <Link to="/settings/modules" className="mb-2 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft size={14} /> Module
        </Link>
        <h1 className={pageTitle}>Plätze: {name}</h1>
      </div>

      {fehler && (
        <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{fehler}</div>
      )}

      {/* „Alle Benutzer" heißt: nichts zu verteilen. Das ist kein Fehler,
          sondern der häufigere Fall — er braucht nur eine Erklärung. */}
      {stand?.module.seatMode === 'ALL_USERS' && (
        <div className={formCard}>
          <p className="text-sm text-foreground">
            Dieses Modul steht auf <strong>alle Benutzer</strong> — jeder in deinem Betrieb
            hat Zugang, und es gibt keine Plätze zu verteilen.
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            Eine Begrenzung auf einzelne Mitarbeiter richtet der Betreiber ein.
          </p>
        </div>
      )}

      {stand?.module.seatMode === 'ASSIGNED' && (
        <>
          <div className={formCard}>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-sm">
                <Users size={16} className="text-muted-foreground" />
                <span className="font-medium text-foreground">
                  {auswahl.size} von {stand.module.seats} Plätzen vergeben
                </span>
                {frei !== null && frei > 0 && (
                  <span className="text-muted-foreground">· {frei} frei</span>
                )}
                {frei !== null && frei < 0 && (
                  <span className="font-medium text-destructive">
                    · {Math.abs(frei)} zu viel
                  </span>
                )}
                {stand.module.seatsDormant > 0 && (
                  <span className="text-muted-foreground">
                    · {stand.module.seatsDormant} ruhend (deaktivierte Benutzer)
                  </span>
                )}
              </div>

              <div className="flex items-center gap-2">
                {gespeichert && !geaendert && (
                  <span className="flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
                    <Check size={14} /> Gespeichert
                  </span>
                )}
                <button
                  type="button"
                  disabled={speichert || !geaendert || (frei !== null && frei < 0)}
                  onClick={() => void speichern()}
                  className="rounded-md border border-primary/30 px-3 py-1.5 text-sm font-medium text-primary transition-colors hover:bg-primary/10 disabled:opacity-40"
                >
                  {speichert ? 'Wird gespeichert…' : 'Zuteilung speichern'}
                </button>
              </div>
            </div>
          </div>

          <div className={formCard}>
            <ul className="divide-y divide-border/50">
              {stand.users.map((u) => {
                const an = auswahl.has(u.id)
                // Ist kein Platz mehr frei, lässt sich niemand Neues anhaken —
                // die Sperre steht hier schon, damit niemand erst beim Speichern
                // erfährt, dass es nicht geht.
                const blockiert = !an && frei !== null && frei <= 0

                return (
                  <li key={u.id} className="flex items-center gap-3 py-2">
                    <input
                      id={`seat-${u.id}`}
                      type="checkbox"
                      checked={an}
                      disabled={blockiert}
                      onChange={() => umschalten(u.id)}
                      className="h-4 w-4 rounded border-border accent-[hsl(var(--primary))] disabled:opacity-40"
                    />
                    <label
                      htmlFor={`seat-${u.id}`}
                      className={`flex-1 cursor-pointer text-sm ${blockiert ? 'text-muted-foreground' : 'text-foreground'}`}
                    >
                      {[u.firstName, u.lastName].filter(Boolean).join(' ') || u.email}
                      <span className="ml-2 text-xs text-muted-foreground">{u.email}</span>
                    </label>
                    <span className="text-xs text-muted-foreground">
                      {u.roles.map((r) => r.name).join(', ')}
                    </span>
                  </li>
                )
              })}
            </ul>

            {stand.users.length === 0 && (
              <p className="py-4 text-center text-sm text-muted-foreground">
                Keine aktiven Benutzer.
              </p>
            )}
          </div>

          <p className="text-xs text-muted-foreground">
            Ein entzogener Platz löscht nichts: Die Zeitbuchungen und Berichte des
            Mitarbeiters bleiben vollständig erhalten, er kommt nur nicht mehr an
            das Modul. Neu eingeladene Mitarbeiter erscheinen hier, sobald sie die
            Einladung angenommen haben.
          </p>
        </>
      )}
    </div>
  )
}
